import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../api';
import { useOutletContext } from 'react-router-dom'; // To get token if needed for API calls

const initialContactData = {
  mainContactInfo: {
    address: '',
    phone: '',
    email: ''
  },
  categories: []
};

export default function ContactAdminPage() {
  const [contactData, setContactData] = useState(initialContactData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const { token } = useOutletContext(); // Access token from AdminDashboard

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/contacts`);
      if (!response.ok) {
        throw new Error(`Failed to fetch contact data: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setContactData(data || initialContactData);
    } catch (err) {
      console.error('Error fetching contact data:', err);
      setError(err.message);
      setContactData(initialContactData); // Reset to initial on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMainContactChange = (e) => {
    const { name, value } = e.target;
    setContactData(prev => ({
      ...prev,
      mainContactInfo: {
        ...prev.mainContactInfo,
        [name]: value
      }
    }));
  };

  const handleCategoryNameChange = (categoryIndex, newName) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], name: newName };
      return { ...prev, categories: updatedCategories };
    });
  };

  const addCategory = () => {
    setContactData(prev => ({
      ...prev,
      categories: [...prev.categories, { id: `new_cat_${Date.now()}`, name: 'New Category', contacts: [] }]
    }));
  };

  const deleteCategory = (categoryIndex) => {
    setContactData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, index) => index !== categoryIndex)
    }));
  };

  const handleContactChange = (categoryIndex, contactIndex, field, value) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      const updatedContacts = [...updatedCategories[categoryIndex].contacts];
      updatedContacts[contactIndex] = { ...updatedContacts[contactIndex], [field]: value };
      updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], contacts: updatedContacts };
      return { ...prev, categories: updatedCategories };
    });
  };

  const handleContactDetailChange = (categoryIndex, contactIndex, detailIndex, value) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      const updatedContacts = [...updatedCategories[categoryIndex].contacts];
      const updatedDetails = [...updatedContacts[contactIndex].details];
      updatedDetails[detailIndex] = value;
      updatedContacts[contactIndex] = { ...updatedContacts[contactIndex], details: updatedDetails };
      updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], contacts: updatedContacts };
      return { ...prev, categories: updatedCategories };
    });
  };

  const addContactDetail = (categoryIndex, contactIndex) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      const updatedContacts = [...updatedCategories[categoryIndex].contacts];
      const updatedDetails = [...(updatedContacts[contactIndex].details || []), 'New Detail'];
      updatedContacts[contactIndex] = { ...updatedContacts[contactIndex], details: updatedDetails };
      updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], contacts: updatedContacts };
      return { ...prev, categories: updatedCategories };
    });
  };

 const deleteContactDetail = (categoryIndex, contactIndex, detailIndex) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      const updatedContacts = [...updatedCategories[categoryIndex].contacts];
      const updatedDetails = updatedContacts[contactIndex].details.filter((_, idx) => idx !== detailIndex);
      updatedContacts[contactIndex] = { ...updatedContacts[contactIndex], details: updatedDetails };
      updatedCategories[categoryIndex] = { ...updatedCategories[categoryIndex], contacts: updatedContacts };
      return { ...prev, categories: updatedCategories };
    });
  };

  const addContact = (categoryIndex) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      const newContact = { id: `new_contact_${Date.now()}`, title: 'New Contact Title', details: ['New Detail Line'] };
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        contacts: [...updatedCategories[categoryIndex].contacts, newContact]
      };
      return { ...prev, categories: updatedCategories };
    });
  };

  const deleteContact = (categoryIndex, contactIndex) => {
    setContactData(prev => {
      const updatedCategories = [...prev.categories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        contacts: updatedCategories[categoryIndex].contacts.filter((_, index) => index !== contactIndex)
      };
      return { ...prev, categories: updatedCategories };
    });
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setSaveMessage('');
    setError(null);
    try {
      const response = await fetch(`${API_URL}/contacts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(contactData)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to save data. Unknown error.' }));
        throw new Error(errorData.message || `Failed to save contact data: ${response.status} ${response.statusText}`);
      }
      const result = await response.json();
      setSaveMessage(result.message || 'Contacts saved successfully!');
      // Optionally re-fetch data to confirm or rely on optimistic update
      // fetchData(); 
    } catch (err) {
      console.error('Error saving contact data:', err);
      setError(err.message);
      setSaveMessage('');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(''), 5000); // Clear message after 5s
    }
  };

  if (loading) return <div className="text-center p-8">Loading Contact Editor...</div>;
  // Error display handled by the main save error state, or initial load error.

  return (
    <div className="container mx-auto p-4 text-white min-h-screen relative">
      <h1 className="text-2xl font-semibold mb-6 text-center pt-4">Edit Contact Page Content</h1>
      
      {error && <div className="bg-red-800/80 text-white p-4 rounded-lg mb-6 text-center max-w-3xl mx-auto">Error: {error}</div>}
      {saveMessage && <div className="bg-green-800/80 text-white p-4 rounded-lg mb-6 text-center max-w-3xl mx-auto">{saveMessage}</div>}

      {/* Main Contact Info Section */}
      <div className="mb-8 p-6 rounded-lg border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-700">Main Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="mainAddress" className="block text-sm font-medium text-gray-300">Address</label>
            <input 
              type="text" 
              id="mainAddress" 
              name="address"
              value={contactData.mainContactInfo.address}
              onChange={handleMainContactChange}
              className="w-full p-3 border border-gray-700 rounded bg-gray-800 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="mainPhone" className="block text-sm font-medium text-gray-300">Phone</label>
            <input 
              type="text" 
              id="mainPhone" 
              name="phone"
              value={contactData.mainContactInfo.phone}
              onChange={handleMainContactChange}
              className="w-full p-3 border border-gray-700 rounded bg-gray-800 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <label htmlFor="mainEmail" className="block text-sm font-medium text-gray-300">Email</label>
            <input 
              type="email" 
              id="mainEmail" 
              name="email"
              value={contactData.mainContactInfo.email}
              onChange={handleMainContactChange}
              className="w-full p-3 border border-gray-700 rounded bg-gray-800 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-16 p-6 rounded-lg border border-gray-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 pb-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold mb-3 sm:mb-0">Contact Categories</h2>
          <button 
            onClick={addCategory} 
            className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-1.5 px-4 rounded text-sm w-full sm:w-auto text-center border border-gray-600"
          >
            + Add Category
          </button>
        </div>
        
        <div className="space-y-6">
          {contactData.categories.map((category, catIndex) => (
            <div key={category.id || catIndex} className="p-5 bg-gray-800/30 rounded-lg border border-gray-700/50">
              {/* Category Header */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <input 
                  type="text" 
                  value={category.name}
                  onChange={(e) => handleCategoryNameChange(catIndex, e.target.value)}
                  className="flex-1 p-2 text-base font-medium border border-gray-600 rounded text-white focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Category Name"
                />
                <div className="flex gap-2">
                  <button 
                    onClick={() => deleteCategory(catIndex)} 
                    className="bg-red-900/80 hover:bg-red-800 text-white font-medium py-1 px-3 rounded text-sm whitespace-nowrap border border-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {/* Contacts List */}
              <div className="space-y-4 pl-2 border-l-2 border-gray-700 ml-2 mt-6">
                {category.contacts.map((contact, contactIdx) => (
                  <div key={contact.id || contactIdx} className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/30">
                    {/* Contact Header */}
                    <div className="flex flex-col sm:flex-row gap-2 mb-2">
                      <input 
                        type="text" 
                        value={contact.title}
                        onChange={(e) => handleContactChange(catIndex, contactIdx, 'title', e.target.value)}
                        className="flex-1 p-2 text-sm font-medium border border-gray-600 rounded text-white focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Contact Title"
                      />
                      <button 
                        onClick={() => deleteContact(catIndex, contactIdx)} 
                        className="bg-red-900/80 hover:bg-red-800 text-white font-medium py-1 px-3 rounded text-sm whitespace-nowrap border border-red-800"
                      >
                        Delete
                      </button>
                    </div>
                    
                    {/* Contact Details */}
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-400">Details:</p>
                      <div className="space-y-2">
                        {contact.details && contact.details.map((detail, detailIdx) => (
                          <div key={detailIdx} className="flex gap-2">
                            <input 
                              type="text" 
                              value={detail}
                              onChange={(e) => handleContactDetailChange(catIndex, contactIdx, detailIdx, e.target.value)}
                              className="flex-1 p-2 border border-gray-600 rounded text-white focus:ring-1 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder={`Detail line ${detailIdx + 1}`}
                            />
                            <button 
                              onClick={() => deleteContactDetail(catIndex, contactIdx, detailIdx)} 
                              className="bg-red-900/80 hover:bg-red-800 text-white font-medium px-3 rounded text-sm border border-red-800"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button 
                          onClick={() => addContactDetail(catIndex, contactIdx)} 
                          className="mt-1 text-xs text-purple-400 hover:text-purple-300 font-medium flex items-center gap-1"
                        >
                          <span>+</span> Add Detail Line
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Contact Button */}
                <button 
                  onClick={() => addContact(catIndex)} 
                  className="mt-1 text-xs text-gray-400 hover:text-white font-medium flex items-center gap-1 ml-2"
                >
                  <span className="text-base">+</span> Add Contact to "{category.name}"
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button 
          onClick={handleSaveChanges} 
          disabled={saving}
          className="bg-purple-700 hover:bg-purple-600 text-white font-medium py-2.5 px-6 rounded-lg text-sm shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 border border-purple-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
